let idCounter = 0
export function nextRowKey() {
  idCounter += 1
  return idCounter
}

export function createNode(existingNodes, x = 0, y = 0) {
  return { key: nextRowKey(), id: `N${existingNodes.length + 1}`, x, y }
}

export function createMaterial(existingMaterials) {
  return {
    key: nextRowKey(),
    id: `M${existingMaterials.length + 1}`,
    name: '',
    materialType: 'concrete',
    fck: '',
    E: 210000000,
    specificWeight: '',
  }
}

export function createSection(existingSections, materialId = '') {
  return {
    key: nextRowKey(),
    id: `S${existingSections.length + 1}`,
    name: '',
    shape: 'rectangular',
    b: 20,
    h: 40,
    bf: 40,
    hf: 10,
    bw: 20,
    customA: '',
    customI: '',
    materialId,
    // Only meaningful when the section's material is concrete: distance from each rebar layer to
    // its nearest face (cover + half the bar diameter, in effect), used by concrete-specific checks.
    rebarDistanceBottom: '',
    rebarDistanceTop: '',
  }
}

// T, inverted-T and I sections share the same field names (bf/hf = flange width/thickness, bw =
// web width/thickness, h = total height): a T section has a single flange on top of the web, an
// inverted T has that same flange on the bottom instead, and an I section has it mirrored on both
// ends. A and I only depend on the flange/web rectangles and their combined centroid, not on which
// end the flange sits at, so the inverted-T case reuses the plain-T formula unchanged.
function computeTOrISectionProperties(section) {
  // bf/hf/bw/h are entered in cm (like a rectangular section's b/h); convert before computing A/I
  // so they come out in the m-based unit system the rest of the model (and the API) expects.
  const h = (Number(section.h) || 0) / 100
  const bf = (Number(section.bf) || 0) / 100
  const hf = (Number(section.hf) || 0) / 100
  const bw = (Number(section.bw) || 0) / 100
  const isI = section.shape === 'iSection'
  const webHeight = Math.max(h - (isI ? 2 * hf : hf), 0)
  const Aflange = bf * hf
  const Aweb = bw * webHeight
  const Iweb0 = (bw * webHeight ** 3) / 12

  if (isI) {
    // Symmetric about mid-height: both flanges sit the same distance from the centroid.
    const A = 2 * Aflange + Aweb
    const Iflange = (bf * hf ** 3) / 12 + Aflange * ((h - hf) / 2) ** 2
    return { A, I: 2 * Iflange + Iweb0 }
  }

  const A = Aflange + Aweb
  if (A === 0) return { A: 0, I: 0 }
  const yFlange = webHeight + hf / 2
  const yWeb = webHeight / 2
  const yBar = (Aflange * yFlange + Aweb * yWeb) / A
  const Iflange = (bf * hf ** 3) / 12 + Aflange * (yBar - yFlange) ** 2
  const Iw = Iweb0 + Aweb * (yBar - yWeb) ** 2
  return { A, I: Iflange + Iw }
}

export function computeSectionProperties(section) {
  if (!section) return { A: 0, I: 0 }
  if (section.shape === 'rectangular') {
    // b and h are entered in cm (unlike every other length field, which is in m) since typical
    // beam/column dimensions are awkward decimals in meters; convert before computing A/I so they
    // come out in the m-based unit system the rest of the model (and the API) expects.
    const b = (Number(section.b) || 0) / 100
    const h = (Number(section.h) || 0) / 100
    return { A: b * h, I: (b * h ** 3) / 12 }
  }
  if (section.shape === 'tSection' || section.shape === 'invertedTSection' || section.shape === 'iSection') {
    return computeTOrISectionProperties(section)
  }
  if (section.shape === 'generic') {
    return { A: Number(section.customA) || 0, I: Number(section.customI) || 0 }
  }
  return { A: 0, I: 0 }
}

export function createElement(existingElements, nodeI = '', nodeJ = '', sectionId = '') {
  return {
    key: nextRowKey(),
    id: `E${existingElements.length + 1}`,
    nodeI,
    nodeJ,
    sectionId,
    releaseI: false,
    releaseJ: false,
    // Only meaningful when the element's section uses a concrete material: bottom (as) and top
    // (asPrime) reinforcement area, in cm².
    as: '',
    asPrime: '',
  }
}

export function createSupport(node) {
  return {
    key: nextRowKey(),
    node,
    restrainU: true,
    restrainV: true,
    restrainTheta: true,
    prescribedU: '',
    prescribedV: '',
    prescribedTheta: '',
  }
}

export function createNodalLoad(node, loadCase = '') {
  return { key: nextRowKey(), node, loadCase, Fx: 0, Fy: -10, Mz: 0 }
}

export function createElementLoad(element, loadCase = '') {
  return {
    key: nextRowKey(),
    element,
    loadCase,
    type: 'distributed',
    coordinateSystem: 'local',
    qxI: 0,
    qxJ: 0,
    qyI: -10,
    qyJ: -10,
    position: 0,
    Px: 0,
    Py: -10,
    Mz: 0,
  }
}

export function createLoadCase(existingCases) {
  return { key: nextRowKey(), id: `C${existingCases.length + 1}`, name: '', category: '', selfWeight: false }
}

export function createCombination(existingCombinations) {
  // `factors` maps a static load-case id to its factor (used by the matrix solver). `trainFactors`
  // maps a moving-load train id to its factor: a combination carrying at least one train factor is
  // resolved by the moving-loads endpoint per train position and shows up under that train's view.
  // `elementOverrides` maps an element id to a section id: the matrix solver rebuilds that
  // element's E/A/I from the chosen section for this combination only (e.g. a cracked/reduced
  // section for an ELS check), leaving every other combination and the elements themselves on the
  // default section.
  return {
    key: nextRowKey(),
    id: `COMB${existingCombinations.length + 1}`,
    name: '',
    kind: 'ultimate',
    factors: {},
    trainFactors: {},
    elementOverrides: {},
  }
}

export const DEFAULT_NODES = []

export const DEFAULT_MATERIALS = []

export const DEFAULT_SECTIONS = []

export const DEFAULT_ELEMENTS = []

export const DEFAULT_SUPPORTS = []

export const DEFAULT_LOAD_CASES = []

export const DEFAULT_COMBINATIONS = []

export const DEFAULT_NODAL_LOADS = []

export const DEFAULT_ELEMENT_LOADS = []

export function mapNodesForApi(nodes) {
  return nodes.map((n) => ({ id: n.id, x: Number(n.x), y: Number(n.y) }))
}

// Resolves a section's stiffness/material properties in the shape the matrix solver expects
// (E/A/I plus an optional material.specificWeight), shared by the element mapper and the
// per-combination element-override mapper below since both derive the same fields from a section.
function mapSectionPropertiesForApi(section, materials) {
  const material = materials.find((m) => m.id === section?.materialId)
  const { A, I } = computeSectionProperties(section)
  const hasSpecificWeight =
    material?.specificWeight !== '' && material?.specificWeight !== undefined && material?.specificWeight !== null
  return {
    E: Number(material?.E) || 0,
    A,
    I,
    ...(hasSpecificWeight ? { material: { specificWeight: Number(material.specificWeight) } } : {}),
  }
}

export function mapElementsForApi(elements, sections, materials) {
  return elements.map((el) => {
    const section = sections.find((s) => s.id === el.sectionId)
    return {
      id: el.id,
      nodeI: el.nodeI,
      nodeJ: el.nodeJ,
      ...mapSectionPropertiesForApi(section, materials),
      releaseI: !!el.releaseI,
      releaseJ: !!el.releaseJ,
    }
  })
}

// Converts a combination's `{ elementId: sectionId }` overrides into the matrix solver's
// `elementOverrides` shape, resolving each chosen section's E/A/I (and specificWeight) the same
// way the base element mapping does. Entries with a blank/unresolvable section are skipped, so
// clearing an override (or a dangling reference to a deleted section) just falls back to the
// element's default section on the backend.
export function mapCombinationOverridesForApi(elementOverrides, sections, materials) {
  const result = {}
  Object.entries(elementOverrides || {}).forEach(([elementId, sectionId]) => {
    if (!sectionId) return
    const section = sections.find((s) => s.id === sectionId)
    if (!section) return
    result[elementId] = { sectionId: section.id, ...mapSectionPropertiesForApi(section, materials) }
  })
  return result
}

export function mapSupportsForApi(supports) {
  return supports.map((s) => {
    const support = {
      node: s.node,
      restrainU: !!s.restrainU,
      restrainV: !!s.restrainV,
      restrainTheta: !!s.restrainTheta,
    }
    if (s.prescribedU !== '' && s.prescribedU !== undefined && s.prescribedU !== null) {
      support.prescribedU = Number(s.prescribedU)
    }
    if (s.prescribedV !== '' && s.prescribedV !== undefined && s.prescribedV !== null) {
      support.prescribedV = Number(s.prescribedV)
    }
    if (s.prescribedTheta !== '' && s.prescribedTheta !== undefined && s.prescribedTheta !== null) {
      support.prescribedTheta = Number(s.prescribedTheta)
    }
    return support
  })
}

// Drops any per-combination elementOverrides entry for an element that no longer exists, so
// deleting an element doesn't leave a stale sectionId override behind for the same id if a new,
// unrelated element later reuses it.
function stripElementOverrides(combinations, removedElementIds) {
  if (!combinations || removedElementIds.length === 0) return combinations
  return combinations.map((c) => {
    if (!c.elementOverrides || Object.keys(c.elementOverrides).length === 0) return c
    const elementOverrides = { ...c.elementOverrides }
    let changed = false
    removedElementIds.forEach((id) => {
      if (id in elementOverrides) {
        delete elementOverrides[id]
        changed = true
      }
    })
    return changed ? { ...c, elementOverrides } : c
  })
}

// Removing a node also removes elements attached to it and any support/load referencing it or its elements
export function cascadeRemoveNode(nodeKey, { nodes, elements, supports, nodalLoads, elementLoads, combinations }) {
  const removedNode = nodes.find((n) => n.key === nodeKey)
  const newNodes = nodes.filter((n) => n.key !== nodeKey)
  if (!removedNode) {
    return { nodes: newNodes, elements, supports, nodalLoads, elementLoads, combinations }
  }

  const removedElementIds = elements
    .filter((e) => e.nodeI === removedNode.id || e.nodeJ === removedNode.id)
    .map((e) => e.id)
  const newElements = elements.filter((e) => e.nodeI !== removedNode.id && e.nodeJ !== removedNode.id)
  const newSupports = supports.filter((s) => s.node !== removedNode.id)
  const newNodalLoads = nodalLoads.filter((l) => l.node !== removedNode.id)
  const newElementLoads = elementLoads.filter((l) => !removedElementIds.includes(l.element))
  const newCombinations = stripElementOverrides(combinations, removedElementIds)

  return {
    nodes: newNodes,
    elements: newElements,
    supports: newSupports,
    nodalLoads: newNodalLoads,
    elementLoads: newElementLoads,
    combinations: newCombinations,
  }
}

export function cascadeRemoveElement(elementKey, { elements, elementLoads, combinations }) {
  const removedElement = elements.find((e) => e.key === elementKey)
  const newElements = elements.filter((e) => e.key !== elementKey)
  if (!removedElement) {
    return { elements: newElements, elementLoads, combinations }
  }
  const newElementLoads = elementLoads.filter((l) => l.element !== removedElement.id)
  const newCombinations = stripElementOverrides(combinations, [removedElement.id])
  return { elements: newElements, elementLoads: newElementLoads, combinations: newCombinations }
}

function nextFreeId(prefix, takenIds) {
  let n = takenIds.length + 1
  while (takenIds.includes(`${prefix}${n}`)) n += 1
  return `${prefix}${n}`
}

function roundCoordinate(value) {
  return Math.round(value * 1e6) / 1e6
}

// Splits one element into `parts` equal segments joined by new intermediate nodes. The first segment
// keeps the original element id (so combination overrides, moving-load paths and the user's mental
// model of "that bar" stay valid) and inherits releaseI; the last one inherits releaseJ; every
// segment keeps the section and reinforcement. Element loads are redistributed: a distributed load
// is interpolated onto each segment's own I/J ends, and a point load goes to the segment that
// contains it (re-based to that segment's origin). Per-combination section overrides and moving-load
// path entries that pointed at the original element are extended to the new segments.
export function splitElement(elementKey, parts, { nodes, elements, elementLoads, combinations, movingLoadsPath }) {
  const count = Math.floor(Number(parts))
  const original = elements.find((e) => e.key === elementKey)
  const nodeI = original && nodes.find((n) => n.id === original.nodeI)
  const nodeJ = original && nodes.find((n) => n.id === original.nodeJ)
  if (!original || !nodeI || !nodeJ || !(count >= 2)) return null

  const xI = Number(nodeI.x)
  const yI = Number(nodeI.y)
  const dx = Number(nodeJ.x) - xI
  const dy = Number(nodeJ.y) - yI
  const length = Math.hypot(dx, dy)

  const takenNodeIds = nodes.map((n) => n.id)
  const newNodes = []
  for (let i = 1; i < count; i += 1) {
    const id = nextFreeId('N', takenNodeIds)
    takenNodeIds.push(id)
    newNodes.push({
      ...createNode(nodes, roundCoordinate(xI + (dx * i) / count), roundCoordinate(yI + (dy * i) / count)),
      id,
    })
  }
  const chainIds = [original.nodeI, ...newNodes.map((n) => n.id), original.nodeJ]

  const takenElementIds = elements.map((e) => e.id)
  const segments = []
  for (let i = 0; i < count; i += 1) {
    if (i === 0) {
      segments.push({ ...original, nodeJ: chainIds[1], releaseJ: false })
    } else {
      const id = nextFreeId('E', takenElementIds)
      takenElementIds.push(id)
      segments.push({
        ...original,
        key: nextRowKey(),
        id,
        nodeI: chainIds[i],
        nodeJ: chainIds[i + 1],
        releaseI: false,
        releaseJ: i === count - 1 ? !!original.releaseJ : false,
      })
    }
  }

  const segmentLength = length / count
  const lerp = (a, b, t) => Number(a) + (Number(b) - Number(a)) * t
  const redistributedLoads = []
  elementLoads
    .filter((l) => l.element === original.id)
    .forEach((load) => {
      if (load.type === 'point') {
        const position = Number(load.position) || 0
        const index = segmentLength > 0 ? Math.min(Math.floor(position / segmentLength + 1e-9), count - 1) : 0
        redistributedLoads.push({
          ...load,
          key: index === 0 ? load.key : nextRowKey(),
          element: segments[index].id,
          position: roundCoordinate(position - index * segmentLength),
        })
        return
      }
      segments.forEach((segment, index) => {
        const t0 = index / count
        const t1 = (index + 1) / count
        redistributedLoads.push({
          ...load,
          key: index === 0 ? load.key : nextRowKey(),
          element: segment.id,
          qxI: roundCoordinate(lerp(load.qxI, load.qxJ, t0)),
          qxJ: roundCoordinate(lerp(load.qxI, load.qxJ, t1)),
          qyI: roundCoordinate(lerp(load.qyI, load.qyJ, t0)),
          qyJ: roundCoordinate(lerp(load.qyI, load.qyJ, t1)),
        })
      })
    })

  const newElements = elements.flatMap((e) => (e.key === elementKey ? segments : [e]))
  const newElementLoads = [...elementLoads.filter((l) => l.element !== original.id), ...redistributedLoads]
  const newCombinations = combinations.map((c) => {
    const overrideSection = c.elementOverrides?.[original.id]
    if (!overrideSection) return c
    const elementOverrides = { ...c.elementOverrides }
    segments.slice(1).forEach((segment) => {
      elementOverrides[segment.id] = overrideSection
    })
    return { ...c, elementOverrides }
  })
  const newPathSegments = (movingLoadsPath?.segments || []).flatMap((s) => {
    if (s.element !== original.id) return [s]
    const expanded = segments.map((segment, index) =>
      index === 0 ? { ...s, element: segment.id } : { key: nextRowKey(), element: segment.id, reverse: !!s.reverse }
    )
    return s.reverse ? expanded.reverse() : expanded
  })

  return {
    nodes: [...nodes, ...newNodes],
    elements: newElements,
    elementLoads: newElementLoads,
    combinations: newCombinations,
    movingLoadsPath: movingLoadsPath ? { ...movingLoadsPath, segments: newPathSegments } : movingLoadsPath,
    firstSegmentKey: segments[0].key,
  }
}

// Removing a material unassigns it from any section that referenced it (left for the user to fix,
// surfaced by validation) rather than cascading further into elements.
export function cascadeRemoveMaterial(materialKey, { materials, sections }) {
  const removedMaterial = materials.find((m) => m.key === materialKey)
  const newMaterials = materials.filter((m) => m.key !== materialKey)
  if (!removedMaterial) {
    return { materials: newMaterials, sections }
  }
  const newSections = sections.map((s) => (s.materialId === removedMaterial.id ? { ...s, materialId: '' } : s))
  return { materials: newMaterials, sections: newSections }
}

// Removing a section unassigns it from any element that referenced it (left for the user to fix,
// surfaced by validation) rather than deleting the element itself.
export function cascadeRemoveSection(sectionKey, { sections, elements }) {
  const removedSection = sections.find((s) => s.key === sectionKey)
  const newSections = sections.filter((s) => s.key !== sectionKey)
  if (!removedSection) {
    return { sections: newSections, elements }
  }
  const newElements = elements.map((el) => (el.sectionId === removedSection.id ? { ...el, sectionId: '' } : el))
  return { sections: newSections, elements: newElements }
}

// Renaming a material's id must follow into every section's materialId, or the rename silently
// breaks that reference (the section keeps pointing at an id that no longer exists) even though
// nothing about the material's actual properties changed.
export function cascadeRenameMaterial(oldId, newId, { sections }) {
  if (oldId === newId) return { sections }
  const newSections = sections.map((s) => (s.materialId === oldId ? { ...s, materialId: newId } : s))
  return { sections: newSections }
}

// Renaming a section's id must follow into every element's sectionId and every combination's
// elementOverrides (which stores a sectionId as the override value), for the same reason as
// cascadeRenameMaterial above.
export function cascadeRenameSection(oldId, newId, { elements, combinations }) {
  if (oldId === newId) return { elements, combinations }
  const newElements = elements.map((el) => (el.sectionId === oldId ? { ...el, sectionId: newId } : el))
  const newCombinations = combinations.map((c) => {
    if (!c.elementOverrides || Object.keys(c.elementOverrides).length === 0) return c
    let changed = false
    const elementOverrides = Object.fromEntries(
      Object.entries(c.elementOverrides).map(([elementId, sectionId]) => {
        if (sectionId !== oldId) return [elementId, sectionId]
        changed = true
        return [elementId, newId]
      })
    )
    return changed ? { ...c, elementOverrides } : c
  })
  return { elements: newElements, combinations: newCombinations }
}

// Removing a load case unassigns it from any nodal/element load that referenced it (left for the
// user to fix, surfaced by validation) and drops it from any combination's factors.
export function cascadeRemoveLoadCase(caseKey, { loadCases, combinations, nodalLoads, elementLoads }) {
  const removedCase = loadCases.find((c) => c.key === caseKey)
  const newLoadCases = loadCases.filter((c) => c.key !== caseKey)
  if (!removedCase) {
    return { loadCases: newLoadCases, combinations, nodalLoads, elementLoads }
  }
  const newNodalLoads = nodalLoads.map((l) => (l.loadCase === removedCase.id ? { ...l, loadCase: '' } : l))
  const newElementLoads = elementLoads.map((l) => (l.loadCase === removedCase.id ? { ...l, loadCase: '' } : l))
  const newCombinations = combinations.map((c) => {
    if (!(removedCase.id in (c.factors || {}))) return c
    const factors = { ...c.factors }
    delete factors[removedCase.id]
    return { ...c, factors }
  })
  return {
    loadCases: newLoadCases,
    combinations: newCombinations,
    nodalLoads: newNodalLoads,
    elementLoads: newElementLoads,
  }
}

// Renaming a load case's id must follow into every nodal/element load's loadCase and into every
// combination's factors key (factors is keyed by case id), same reasoning as the material/section
// renames above: the case's actual loads/factor didn't change, only its id.
export function cascadeRenameLoadCase(oldId, newId, { nodalLoads, elementLoads, combinations }) {
  if (oldId === newId) return { nodalLoads, elementLoads, combinations }
  const newNodalLoads = nodalLoads.map((l) => (l.loadCase === oldId ? { ...l, loadCase: newId } : l))
  const newElementLoads = elementLoads.map((l) => (l.loadCase === oldId ? { ...l, loadCase: newId } : l))
  const newCombinations = combinations.map((c) => {
    if (!(oldId in (c.factors || {}))) return c
    const factors = { ...c.factors }
    factors[newId] = factors[oldId]
    delete factors[oldId]
    return { ...c, factors }
  })
  return { nodalLoads: newNodalLoads, elementLoads: newElementLoads, combinations: newCombinations }
}
